'use client';

import { ChevronDown, Copy, Mail } from 'lucide-react';
import { useMemo } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/navigation/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { EmailCopy } from './EmailCopy';
import { EmailValidationStatus, type EmailValidationStatusValue } from './EmailValidationStatus';
import type { ContactEmailValidationMap } from '@/lib/contact-email-validation';
import { normalizeEmailForValidation } from '@/lib/contact-email-validation';
import { useEventTracking } from '@/hooks/useEventTracking';
import { EventType } from '@/api/types/events';

interface EmailCopyWithDropdownProps {
  email?: string | null;
  emails?: string[];
  className?: string;
  textClassName?: string;
  iconClassName?: string;
  showOnHover?: boolean;
  emailValidations?: ContactEmailValidationMap;
  onReachOutEmail?: (email: string) => void;
}

const normalizeEmails = (values: Array<string | null | undefined>) =>
  Array.from(
    new Set(
      values
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map((value) => value.trim())
    )
  );

export function EmailCopyWithDropdown({
  email,
  emails = [],
  className,
  textClassName,
  iconClassName,
  showOnHover = true,
  emailValidations,
  onReachOutEmail,
}: EmailCopyWithDropdownProps) {
  const { toast } = useToast();
  const { trackEvent } = useEventTracking();
  const allEmails = useMemo(
    () => normalizeEmails([email, ...emails]),
    [email, emails]
  );

  if (!allEmails.length) {
    return null;
  }

  const primaryEmail = allEmails[0];
  const hasAlternatives = allEmails.length > 1;
  const getValidationStatus = (targetEmail: string): EmailValidationStatusValue | null => {
    const normalized = normalizeEmailForValidation(targetEmail);
    if (!normalized) {
      return null;
    }
    return (emailValidations?.[normalized]?.status as EmailValidationStatusValue | undefined) ?? null;
  };

  const copySelectedEmail = async (selectedEmail: string) => {
    trackEvent({
      event_type: EventType.CLICK,
      element_id: 'email-copy',
      event_details: {
        action: 'email_copy_click',
        funnel: 'contact_outreach',
        source: 'email_copy_dropdown',
        email: selectedEmail,
      },
    });
    try {
      await navigator.clipboard.writeText(selectedEmail);
      toast({
        title: 'Email copied to clipboard',
        description: selectedEmail,
        duration: 2000,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed to copy',
        description: 'Please try again',
      });
    }
  };

  const handleReachOutWithEmail = (
    event: React.MouseEvent<HTMLButtonElement>,
    selectedEmail: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    trackEvent({
      event_type: EventType.CLICK,
      element_id: 'email-reach-out',
      event_details: {
        action: 'reach_out_with_email_click',
        funnel: 'contact_outreach',
        source: 'email_copy_dropdown',
        email: selectedEmail,
      },
    });
    onReachOutEmail?.(selectedEmail);
  };

  const handleCopyWithEmail = (
    event: React.MouseEvent<HTMLButtonElement>,
    selectedEmail: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    void copySelectedEmail(selectedEmail);
  };

  const handleReachOutWithEmailFromText = (
    event: React.MouseEvent<HTMLButtonElement>,
    selectedEmail: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    trackEvent({
      event_type: EventType.CLICK,
      element_id: 'email-reach-out-text',
      event_details: {
        action: 'reach_out_with_email_click',
        funnel: 'contact_outreach',
        source: 'email_copy_dropdown_text',
        email: selectedEmail,
      },
    });
    onReachOutEmail?.(selectedEmail);
  };

  return (
    <span className={cn('inline-flex items-center gap-1 group', className)}>
      <EmailCopy
        email={primaryEmail}
        textClassName={textClassName}
        iconClassName={iconClassName}
        showOnHover={showOnHover}
      />
      {hasAlternatives && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center p-0.5 rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground cursor-pointer'
              )}
              title="Show all emails"
              aria-label="Show all emails"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[280px] max-h-[240px] overflow-y-auto">
            {allEmails.map((listedEmail) => {
              const validationStatus = getValidationStatus(listedEmail);
              return (
                <DropdownMenuItem
                  key={listedEmail}
                  onSelect={
                    onReachOutEmail
                      ? () => {
                          onReachOutEmail(listedEmail);
                        }
                      : undefined
                  }
                  className={cn(
                    'flex items-center justify-between gap-2',
                    onReachOutEmail && 'cursor-pointer'
                  )}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    {onReachOutEmail ? (
                      <button
                        type="button"
                        className="truncate text-left text-xs hover:underline"
                        onClick={(event) => handleReachOutWithEmailFromText(event, listedEmail)}
                        title={`Reach out via ${listedEmail}`}
                        aria-label={`Reach out via ${listedEmail}`}
                      >
                        {listedEmail}
                      </button>
                    ) : (
                      <span className="truncate text-xs">{listedEmail}</span>
                    )}
                    {/* Only surface a REAL, resolved result here (valid/invalid);
                        pending/unknown render nothing so the header never shows a
                        stuck "Checking". Same icon+word treatment as the composer. */}
                    <EmailValidationStatus status={validationStatus} className="shrink-0" />
                  </div>
                  <div className="flex items-center gap-1">
                    {onReachOutEmail && (
                      <button
                        type="button"
                        className="inline-flex h-6 w-6 items-center justify-center rounded border border-border/70 text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={(event) => handleReachOutWithEmail(event, listedEmail)}
                        title={`Reach out via ${listedEmail}`}
                        aria-label={`Reach out via ${listedEmail}`}
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded border border-border/70 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={(event) => handleCopyWithEmail(event, listedEmail)}
                      title={`Copy ${listedEmail}`}
                      aria-label={`Copy ${listedEmail}`}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </span>
  );
}
